import * as SelectPrimitive from '@radix-ui/react-select'
import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export const RELATION_VALUES = [
  'parent',
  'sibling',
  'grandparent',
  'personal_assistant',
  'foster_family',
  'housing_support',
  'self',
] as const

// Not yet a backend-supported relation value — selectable in forms that opt in via
// `includeOther`, paired with a free-text field, until the backend enum adds it.
export const RELATION_OTHER_VALUE = 'other' as const

export type Relation = (typeof RELATION_VALUES)[number] | typeof RELATION_OTHER_VALUE

/**
 * Whose relation is being described. Only `self` differs: picking your own
 * relation reads "Jag själv", while describing someone else's — inviting them,
 * or listing them — reads "Personen det gäller".
 */
export type RelationPerspective = 'first-person' | 'third-person'

const isThirdPersonSelf = (relation: Relation, perspective: RelationPerspective) =>
  relation === 'self' && perspective === 'third-person'

// `as const` keeps these narrowed to literal key unions — `t()` is typed
// against the Swedish translation keys and rejects a plain `string`.
export const relationLabelKey = (relation: Relation, perspective: RelationPerspective) =>
  isThirdPersonSelf(relation, perspective)
    ? ('relation.options.self.otherLabel' as const)
    : (`relation.options.${relation}.label` as const)

export const relationDescriptionKey = (relation: Relation, perspective: RelationPerspective) =>
  isThirdPersonSelf(relation, perspective)
    ? ('relation.options.self.otherDescription' as const)
    : (`relation.options.${relation}.description` as const)

type Props = {
  id?: string
  className?: string
  // Adds the free-text "Annan" option. Backend doesn't accept it yet — only opt in
  // where a paired free-text field (and eventual backend support) exists.
  includeOther?: boolean
  onValueChange(value: Relation): void
  perspective: RelationPerspective
  value?: Relation
}

export const RelationSelect = ({
  className,
  id,
  includeOther,
  onValueChange,
  perspective,
  value,
}: Props) => {
  const { t } = useTranslation()
  const options = includeOther ? [...RELATION_VALUES, RELATION_OTHER_VALUE] : RELATION_VALUES

  return (
    <Select onValueChange={v => onValueChange(v as Relation)} value={value}>
      <SelectTrigger className={className} id={id}>
        <SelectValue placeholder={t('relation.placeholder')} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options.map(option => (
            <SelectPrimitive.Item
              className="relative flex w-full cursor-default select-none flex-col items-start rounded-sm py-2 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              key={option}
              value={option}
            >
              <SelectPrimitive.ItemText>
                {t(relationLabelKey(option, perspective))}
              </SelectPrimitive.ItemText>
              <span className="text-xs text-muted-foreground">
                {t(relationDescriptionKey(option, perspective))}
              </span>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 flex h-3.5 w-3.5 items-center justify-center">
                <SelectPrimitive.ItemIndicator>
                  <Check className="h-4 w-4" />
                </SelectPrimitive.ItemIndicator>
              </span>
            </SelectPrimitive.Item>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}
